import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import api from "../api";

const fetchMonthly = (months) => api.get("/sales/trend", { params: { months } }).then(r => r.data);
const fetchWeekly = (weeks) => api.get("/sales/trend/weekly", { params: { weeks } }).then(r => r.data);

const fmt = (n) => `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(n)}`;

export default function SalesTrend() {
  const [view, setView] = useState("weekly");
  const [months, setMonths] = useState(6);
  const [weeks, setWeeks] = useState(12);

  const { data: monthlyData, isLoading: monthlyLoading } = useQuery({
    queryKey: ["trend-monthly", months],
    queryFn: () => fetchMonthly(months),
    enabled: view === "monthly"
  });

  const { data: weeklyData, isLoading: weeklyLoading } = useQuery({
    queryKey: ["trend-weekly", weeks],
    queryFn: () => fetchWeekly(weeks),
    enabled: view === "weekly"
  });

  const data = view === "weekly" ? weeklyData : monthlyData;
  const isLoading = view === "weekly" ? weeklyLoading : monthlyLoading;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">Sales Trend</div>
          <div className="page-subtitle">Revenue and units over time</div>
        </div>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          {/* View toggle */}
          <div style={{ display: "flex", background: "#f0f0f0", borderRadius: "8px", padding: "3px" }}>
            {["weekly", "monthly"].map(v => (
              <button key={v} onClick={() => setView(v)}
                style={{
                  padding: "6px 16px", borderRadius: "6px", border: "none", cursor: "pointer",
                  fontWeight: 600, fontSize: "0.85rem",
                  background: view === v ? "white" : "transparent",
                  color: view === v ? "#4361ee" : "#888",
                  boxShadow: view === v ? "0 1px 3px rgba(0,0,0,0.1)" : "none"
                }}>
                {v === "weekly" ? "Weekly" : "Monthly"}
              </button>
            ))}
          </div>

          {/* Range selector */}
          {view === "monthly" ? (
            <select value={months} onChange={e => setMonths(Number(e.target.value))}
              style={{ padding: "7px 12px", border: "1px solid #e0e0e0", borderRadius: "8px", fontSize: "0.875rem" }}>
              <option value={3}>Last 3 months</option>
              <option value={6}>Last 6 months</option>
              <option value={12}>Last 12 months</option>
            </select>
          ) : (
            <select value={weeks} onChange={e => setWeeks(Number(e.target.value))}
              style={{ padding: "7px 12px", border: "1px solid #e0e0e0", borderRadius: "8px", fontSize: "0.875rem" }}>
              <option value={8}>Last 8 weeks</option>
              <option value={12}>Last 12 weeks</option>
              <option value={24}>Last 24 weeks</option>
            </select>
          )}
        </div>
      </div>

      {isLoading ? <div className="loading">Loading...</div> : (
        <>
          <div className="chart-card">
            <div className="chart-title">
              {view === "weekly" ? "Weekly" : "Monthly"} Revenue (₹)
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey={view === "weekly" ? "week" : "month"} tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                <Tooltip formatter={v => fmt(v)} />
                <Bar dataKey="revenue" name="Revenue" fill="#4361ee" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="chart-card">
            <div className="chart-title">
              {view === "weekly" ? "Weekly" : "Monthly"} Units Sold
            </div>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey={view === "weekly" ? "week" : "month"} tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line type="monotone" dataKey="units" name="Units" stroke="#38a169" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="orders" name="Orders" stroke="#ed8936" strokeWidth={2} dot={{ r: 3 }} />
                <Legend />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="table-card">
            <div className="table-header">
              <span style={{ fontWeight: 600 }}>
                {view === "weekly" ? "Weekly" : "Monthly"} breakdown
              </span>
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>{view === "weekly" ? "Week" : "Month"}</th>
                    <th>Revenue</th>
                    <th>Units</th>
                    <th>Orders</th>
                    <th>Avg Order Value</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.map((row, i) => (
                    <tr key={i}>
                      <td>{view === "weekly" ? row.week : row.month}</td>
                      <td>{fmt(row.revenue)}</td>
                      <td>{row.units?.toLocaleString()}</td>
                      <td>{row.orders?.toLocaleString()}</td>
                      <td>{fmt(row.orders > 0 ? row.revenue / row.orders : 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
